export type SpatialAssetRole = 'image' | 'map_descriptor' | 'tracking_manifest';
export type SpatialConfigurationState = 'NONE' | 'INCOMPLETE' | 'INVALID' | 'VALIDATION_PENDING' | 'VERIFICATION_PENDING' | 'INACTIVE' | 'ACTIVE';
export type SpatialStreamStatus = 'NOT_READY' | 'WAITING_FOR_DATA' | 'LIVE' | 'STALE';
export interface SpatialScope { customerId: number; buildingId: string; unitId: string; }
export interface SpatialIssue { code: string; message: string; role: SpatialAssetRole | null; path: string | null; sensorId: string | null; }
export interface SpatialAsset {
  assetId: string; role: SpatialAssetRole; originalFilename: string; contentType: string;
  sizeBytes: number; sha256: string; uploadStatus: 'UPLOADED';
}
export interface SpatialRevision {
  revisionId: string; installation_id: string; baseActiveRevisionId: string | null;
  savedAt: string | null; activatedAt: string | null;
  assets: Record<SpatialAssetRole, SpatialAsset | null>;
  validationStatus: 'PENDING' | 'INCOMPLETE' | 'INVALID' | 'VALID';
  compatibilityStatus: 'UNVERIFIED' | 'VERIFIED' | 'INCOMPATIBLE';
  missingRequirements: SpatialIssue[]; validationErrors: SpatialIssue[];
}
export interface SpatialMetadata {
  mapPresent: boolean; mapUsable: boolean; trackingReady: boolean;
  configurationState: SpatialConfigurationState; blockingCode: string | null; message: string | null;
  activeConfigRevisionId: string | null; savedMapRevisionId: string | null;
  operationalMissingRequirements: SpatialIssue[]; operationalValidationErrors: SpatialIssue[];
}
export interface SpatialUnitMetadata extends SpatialMetadata {
  draft: { draftId: string; saved: boolean; hasMap: boolean; validationStatus: SpatialRevision['validationStatus']; compatibilityStatus: SpatialRevision['compatibilityStatus']; } | null;
}
export interface SpatialEditor extends SpatialMetadata, SpatialScope {
  schema_version: 1; draftVersion: number; selectedInstallationId: string | null;
  active: SpatialRevision | null; retained: SpatialRevision | null; draft: SpatialRevision | null;
}
export interface SpatialAvailability {
  buildingId: string; spatialTrackingEnabled: boolean; canUseSpatialView: boolean;
  unitsWithMapCount: number; mapUnitIds: string[]; defaultSpatialUnitId: string | null;
}
export interface SpatialInstallation {
  installation_id: string; calibration_set_id: string | null; verificationStatus: SpatialVerificationStatus;
  coordinate_systems: SpatialCoordinateSystems | null;
  installationVersion?: number; installationETag?: string;
  sensors: { sensorId: string; deviceId: string | null; deviceLabel: string | null; assignedUnitId: string | null; available: boolean; }[];
}
export type SpatialVerificationStatus = 'VERIFIED' | 'UNVERIFIED';
export interface SpatialCoordinateSystems { sensor_input: string; global_output: string; input_already_floor_levelled: boolean; }
export interface SpatialProducerProfile { calibration_set_id: string | null; coordinate_systems: SpatialCoordinateSystems | null; }
export interface SpatialAttestation {
  userId?: number | null; verifiedBy?: string | null; verifiedAt?: string | null; reference?: string | null; digest?: string | null;
}
export interface SpatialInstallationState {
  installation_id: string; installationVersion: number; installationETag: string;
  profile: SpatialProducerProfile; profileStatus: 'MISSING' | 'CONFIGURED';
  verificationStatus: SpatialVerificationStatus; producerAttestation: SpatialAttestation | null;
  affectedUnitIds: string[]; blockingActiveUnitIds: string[];
}
export interface SpatialInstallationEditor extends SpatialInstallationState {
  bindings: { sensorId: string; deviceId: string; deviceLabel: string; unitId: string; }[];
}
export interface SpatialRegistrationSensor {
  sensorId: string; enabled: boolean; expectedDeviceLabel: string | null;
  deviceId: string | null; deviceLabel: string | null; assignedUnitId: string | null;
  coverageStatus: SpatialVerificationStatus;
  coverageAttestation: (SpatialAttestation & { exclusiveCoverageVerified?: boolean; coverageDigest?: string; }) | null;
}
export interface SpatialRegistrationDevice { deviceId: string; deviceLabel: string; available: boolean; assignedSensorId: string | null; }
export interface SpatialRegistrationEditor extends SpatialInstallationState, SpatialScope {
  draftId: string; draftVersion: number;
  candidateProfile: (SpatialProducerProfile & { installation_id: string; }) | null;
  assets: Record<SpatialAssetRole, { assetId: string; sha256: string; } | null>;
  packageDigest: string | null;
  sensors: SpatialRegistrationSensor[]; devices: SpatialRegistrationDevice[];
  producerStatus: SpatialVerificationStatus; packageStatus: SpatialVerificationStatus; coverageStatus: SpatialVerificationStatus;
  packageAttestation: (SpatialAttestation & { assets: SpatialRegistrationEditor['assets']; draftId: string; }) | null;
  missingRequirements: SpatialIssue[]; validationErrors: SpatialIssue[];
}
export interface SpatialPortalMutation {
  outcome: string; installation_id: string; installationVersion: number; installationETag: string;
  draftId?: string; draftVersion?: number; affectedUnitIds: string[]; blockingActiveUnitIds: string[];
  idempotencyReplayed?: boolean;
}
export interface SpatialProfileRequest { unitId: string; draftId: string; draftVersion: number; }
export interface SpatialBindingsRequest { installationVersion: number; bindings: { sensorId: string; deviceId: string; }[]; }
export type SpatialUnitAttestationRequest =
  { installationVersion: number; kind: 'PACKAGE'; reference: string; } |
  { installationVersion: number; kind: 'COVERAGE'; references: { sensorId: string; reference: string; }[]; };
export interface SpatialMutation {
  outcome?: string; draftId?: string; draftVersion: number; draft?: SpatialRevision | null;
  activeConfigRevisionId?: string | null; activeConfigurationPreserved?: boolean;
  missingRequirements?: SpatialIssue[]; validationErrors?: SpatialIssue[];
  error?: { code: string; message: string; retryable: boolean; }; idempotencyReplayed?: boolean;
}
export type SpatialMatrix3x3 = readonly [readonly [number, number, number], readonly [number, number, number], readonly [number, number, number]];
export interface SpatialMapConfig {
  schema_version: number; map_id: string;
  image: { file: string; width_px: number; height_px: number; };
  global_xz_to_pixel: SpatialMatrix3x3;
}
export interface SpatialFrontendConfig extends SpatialUnitMetadata, SpatialScope {
  schema_version: 2; installation_id: string | null; calibration_set_id: string | null;
  configRevisionId: string | null; mapRevisionId: string | null; coordinate_system: string | null;
  map: SpatialMapConfig | null; image_url: string | null;
  stream: { enabled: boolean; path: string | null; status: SpatialStreamStatus; staleAfterMs: number; lastReceivedUtcMs: number | null; };
  missingRequirements: SpatialIssue[]; validationErrors: SpatialIssue[];
}
export interface SpatialEnvelope {
  schema_version: 2; buildingId: string; unitId: string; installation_id: string; calibration_set_id: string;
  configRevisionId: string; mapRevisionId: string; runtimeEpoch: string; timestamp_utc_ms: number;
}
export interface SpatialConnectedMessage extends SpatialEnvelope {
  type: 'spatial_connected.v2'; streamStatus: SpatialStreamStatus; lastReceivedUtcMs: number | null;
}
export interface SpatialPerson {
  id: string; x_m: number; z_m: number; confidence: number; observed_by: string[]; state: 'tracked' | 'held';
  map_position_px: readonly [number, number]; map_position_inside: boolean;
  last_seen_age_s?: number | null; velocity_xz_m_s?: readonly [number, number] | null;
  pose?: string | null; pose_confidence?: number | null; event_state?: string | null;
}
export interface SpatialPeopleSnapshot extends SpatialEnvelope {
  type: 'spatial_people.v2'; sequence: number; generated_at_utc_ms: number; people: SpatialPerson[];
}
export type SpatialSocketMessage = SpatialConnectedMessage | SpatialPeopleSnapshot;

export function spatialBlockingMessage(state: Pick<SpatialMetadata, 'blockingCode' | 'configurationState'>): string {
  switch (state.blockingCode) {
    case 'FILES_MISSING': return 'Completare il caricamento dei file di configurazione della mappa.';
    case 'TECHNICAL_VERIFICATION_PENDING': return 'Configurazione in attesa di verifica tecnica.';
    case 'CONFIGURATION_INACTIVE': return 'Configurazione Spatial disattivata.';
    case 'VALIDATION_REQUIRED': return 'Salvare la configurazione per validare i file caricati.';
    case 'SPATIAL_CONFIGURATION_INVALID': return 'La configurazione della mappa contiene errori da correggere.';
    case 'CONFIGURATION_ABSENT': return 'Nessuna configurazione Spatial salvata per questa unità.';
    default: return state.configurationState === 'ACTIVE' ? 'Configurazione Spatial attiva.' : 'Configurazione Spatial non disponibile.';
  }
}
